import { Helmet } from "react-helmet-async";
import { Phone } from "lucide-react";

const ServiceAssistance = () => {
  return (
    <div className="min-h-screen flex flex-col">
      <Helmet>
        <title>Assistance à la traite - Agroref</title>
        <meta name="description" content="Service d'assistance professionnelle à la traite pour optimiser votre production laitière" />
      </Helmet>
      
      
      <main className="flex-1">
        {/* Hero Section */}
        <section className="bg-gradient-to-br from-primary/10 to-primary/5 py-20">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <h1 className="text-4xl md:text-5xl font-bold text-foreground mb-6">
                Assistance à la traite: Plus de lait avec moins d'efforts
              </h1>
              <p className="text-lg text-muted-foreground mb-6">
                La traite n'est pas seulement une routine nécessaire ; c'est le cœur de votre exploitation laitière. Chez Agro, nous l'appréhendons comme telle. C'est pourquoi nous mettons à votre disposition un service d'assistance à la traite, pour transformer ce moment clé en une opération optimisée, saine et respectueuse pour vos animaux.
              </p>
              <p className="text-lg text-muted-foreground mb-8">
                Imaginez moins de contraintes, plus d'efficacité et des animaux en meilleure santé. C'est l'objectif de notre équipe d'experts, qui mettra son savoir-faire et son expérience à votre service pour améliorer votre processus de traite. Vous concentrer sur votre passion et laisser la technique à nos soins : voilà la promesse de l'assistance à la traite par Agro.
              </p>
              <a
                href="/contact"
                className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-8 py-3 rounded-lg font-semibold hover:bg-primary/90 transition-colors"
              >
                Contactez-nous dès maintenant!
              </a>
            </div>
          </div>
        </section>

        {/* Section: La traite - Un facteur clé */}
        <section className="py-16 bg-background">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <h2 className="text-3xl font-bold mb-8">La traite: Un facteur clé pour la qualité du lait et le bien-être de vos vaches</h2>
              
              <div className="grid md:grid-cols-2 gap-8 text-muted-foreground">
                <div className="space-y-6">
                  <p>
                    <strong>L'art de la traite du lait</strong> est plus complexe que la simple extraction d'un liquide. C'est une danse délicate qui, lorsqu'elle est bien exécutée, peut transformer un produit laitier ordinaire en une délectation pour les clients. <strong>L'hygiène stricte</strong> est la clef de voûte de ce processus, car elle produit un lait riche en nutriments et pauvre en cellules somatiques et bactéries.
                  </p>
                  
                  <p>
                    La traite n'est pas qu'une question de lait. C'est aussi une histoire de <strong>bien-être animal</strong>. Un processus régulier dans un environnement serein et sûr ne stimule pas seulement la production de lait. Il caresse aussi le bien-être et la santé des vaches dans le sens du poil, démontrant un engagement réel envers ces animaux.
                  </p>
                </div>

                <div className="space-y-6">
                  <p>
                    L'attention portée au détail n'est pas qu'un acte de compassion. C'est un investissement, un pacte avec la longévité de vos animaux. Cette méthode assure une <strong>production laitière durable</strong> et économiquement viable, un parfait mélange entre éthique et bénéfices.
                  </p>
                  
                  <p>
                    Enfin, la qualité de la traite influence chaque produit laitier transformé. Que ce soit du fromage, du yaourt, ou du lait en poudre, tous sont le reflet de la qualité du lait brut. En soignant chaque détail de votre processus de traite, vous créez une généalogie de <strong>produits laitiers de haute qualité</strong> qui feront votre fierté et celle de vos clients.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Section: L'assistance à la traite */}
        <section className="py-16 bg-muted/30">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <h2 className="text-3xl font-bold mb-8">L'assistance à la traite: une expertise qui fait la différence</h2>
              
              <div className="grid md:grid-cols-2 gap-8 items-start">
                <div className="space-y-6 text-muted-foreground">
                  <p>
                    Chez Agro, notre maîtrise du domaine des solutions pour les fermes laitières est un atout majeur, fruit d'années d'expérience et de savoir-faire. Reconnus pour la qualité de nos produits et pour notre service à la clientèle de premier ordre, nous avons su nous démarquer en tant que leaders dans notre domaine.
                  </p>
                  
                  <p>
                    Notre offre ne se limite pas à des équipements de haute qualité. Nous proposons également un service d'assistance à la traite pour aider les éleveurs laitiers à optimiser leurs pratiques. L'objectif? Assurer des conditions sanitaires optimales, améliorer la qualité du lait produit et contribuer au bien-être animal.
                  </p>
                  
                  <p>
                    Notre service d'assistance à la traite s'étend de l'inspection du système de traite pour déceler rapidement tout problème, jusqu'à la formation des éleveurs laitiers pour renforcer leur efficacité. Chez Agro, notre fierté réside dans notre engagement à fournir un service exceptionnel pour le bien-être de vos vaches laitières et la qualité de votre lait.
                  </p>
                </div>

                <div className="rounded-lg overflow-hidden">
                  <img 
                    src="https://agroref.ca/wp-content/uploads/2023/07/Agroref_lely-3-scaled.jpg"
                    alt="Assistance à la traite"
                    className="w-full h-auto object-cover"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="bg-primary/5 py-16">
          <div className="container mx-auto px-4 text-center">
            <p className="text-xl font-semibold mb-6">Nous sommes là pour vous!</p>
            <h3 className="text-2xl font-bold mb-4">Besoin d'Assistance à la Traite?</h3>
            <p className="text-lg text-muted-foreground mb-8 max-w-3xl mx-auto">
              Si vous cherchez à optimiser la traite de vos vaches, nous sommes là pour vous aider. Nos experts sont prêts à discuter de vos besoins spécifiques, vous proposer des solutions sur mesure et vous accompagner pour améliorer la qualité de votre lait. Ne laissez pas les défis de la traite vous submerger, faites appel à notre équipe de professionnels dévoués.
            </p>
            <a
              href="/contact"
              className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-8 py-3 rounded-lg font-semibold hover:bg-primary/90 transition-colors"
            >
              <Phone className="h-5 w-5" />
              Contactez-nous dès maintenant!
            </a>
          </div>
        </section>
      </main>
    </div>
  );
};

export default ServiceAssistance;
